import { ComponentPropsWithoutRef, useCallback, useContext, useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { BoxIcon, BoxIconProps } from '@givebutter/primitives/box-icon';
import { Button } from '@givebutter/primitives/button';
import { LoadingOverlay } from '@givebutter/primitives/loading-overlay';
import { Separator } from '@givebutter/primitives/separator';
import {
    faArrowRight,
    faCheck,
    faChevronDown,
    faGridRound2Plus,
    faSearch,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchSegments } from 'api/segments';
import * as Drawer from 'components/drawer';
import * as DropdownMenu from 'components/dropdown-menu';
import * as DeleteSegmentDialog from 'components/filter/filters-next/DeleteSegmentDialog';
import { FilterProviderContext } from 'components/filter/filters-next/FilterProvider';
import { SegmentContext } from 'components/filter/filters-next/SegmentContext';
import { transformSavedFilterGroups } from 'components/filter/filters-next/downstream';
import { ModelType } from 'components/filter/types-legacy';
import * as Input from 'components/input';
import { ascIconFactory, descIconFactory } from 'components/table/util';
import * as Tooltip from 'components/tooltip';
import { HELP_CENTER_LINK } from 'constants/links';
import { useFiltersCache } from 'contexts/useFiltersCache';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { segmentQueryKeys } from 'queries/segment-query-keys';
import { twMerge } from 'tailwind-merge';

type SortState = 'asc' | 'desc' | 'newest' | 'oldest';

interface SegmentsIndexDrawerProps {
    buttonProps?: ComponentPropsWithoutRef<'button'> & { 'data-intercom-target'?: string };
}

const SegmentsIndexDrawer = (props: SegmentsIndexDrawerProps) => {
    const { buttonProps = {} } = props;
    const { className = '', ...restButtonProps } = buttonProps;
    const { accountId } = useCurrentAccount();
    const { resource, cacheKey } = useContext(FilterProviderContext);
    const { setName, setIsEditing } = useContext(SegmentContext);
    const setFilters = useFiltersCache(state => state.updateFiltersNextByKey);
    const setSegmentId = useFiltersCache(state => state.setSegmentIdByKey);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search);
    const [sortState, setSortState] = useState<SortState>('newest');

    const [isPending, params] = useIsPending({
        query: debouncedSearch,
        ...getSortStateForRequest(sortState),
    });

    const { data: savedSegments } = useSuspenseQuery({
        queryKey: segmentQueryKeys.withFilters(accountId, resource as ModelType, params),
        queryFn: () => fetchSegments(accountId, resource as ModelType, params),
    });

    const noSegments = savedSegments.length === 0;
    const noSearch = debouncedSearch.length === 0;

    const EmptySearch = useCallback(() => {
        if (!noSegments || noSearch) {
            return null;
        }

        return (
            <div className={'flex flex-col items-center justify-center gap-4 p-8'}>
                <div
                    className={
                        'flex aspect-square h-12 items-center justify-center rounded-md bg-gray-100'
                    }
                >
                    <FontAwesomeIcon className={'font-medium text-gray-600'} icon={faSearch} />
                </div>
                <p className={'font-medium text-gray-600'}>No segments found</p>
            </div>
        );
    }, [noSearch, noSegments]);

    const EmptySegments = useCallback(() => {
        if (isPending || !noSegments || !noSearch) {
            return null;
        }

        return (
            <div className={'flex flex-col gap-4 rounded-lg bg-gray-50 p-6'}>
                <div className={'flex items-center justify-between gap-4'}>
                    <p className={'font-medium'}>FAQs</p>
                    <a
                        className={'flex items-center gap-1 text-sm font-medium'}
                        href={HELP_CENTER_LINK}
                        target={'_blank'}
                        rel="noreferrer"
                    >
                        Help center
                        <FontAwesomeIcon size={'xs'} icon={faArrowRight} />
                    </a>
                </div>
                <Separator className={'bg-gray-200'} />
                <div className={'flex flex-col gap-6'}>
                    <div className={'flex flex-col'}>
                        <p className={'text-sm font-medium text-gray-700'}>What are segments?</p>
                        <p className={'text-xs text-gray-600'}>
                            Segments are saved groups that you can create by applying specific
                            filters. They help you organize and find your data based on the criteria
                            you define.
                        </p>
                    </div>
                    <div className={'flex flex-col'}>
                        <p className={'text-sm font-medium text-gray-700'}>
                            How do I create a segment?
                        </p>
                        <p className={'text-xs text-gray-600'}>
                            Add the filters to your table, then save and name the filter group as a
                            segment for future use.
                        </p>
                    </div>
                    <div className={'flex flex-col'}>
                        <p className={'text-sm font-medium text-gray-700'}>
                            Are segments automatically updated?
                        </p>
                        <p className={'text-xs text-gray-600'}>
                            Yes. All segments are dynamic and adjust as information is updated or
                            changed.{' '}
                        </p>
                    </div>
                </div>
            </div>
        );
    }, [isPending, noSearch, noSegments]);

    return (
        <Drawer.Root open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
            <Tooltip.Root>
                <Tooltip.Trigger asChild={true}>
                    <Drawer.Trigger
                        className={twMerge(
                            'flex h-8 w-8 items-center justify-center rounded-md p-4 text-gray-500 hover:bg-gray-200',
                            className,
                        )}
                        {...restButtonProps}
                    >
                        <span className="sr-only">Segments</span>
                        <FontAwesomeIcon icon={faGridRound2Plus} />
                    </Drawer.Trigger>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                    <Tooltip.Content inverse={true}>
                        <p>Segments</p>
                        <Tooltip.Arrow />
                    </Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip.Root>
            <Drawer.Content>
                <Drawer.Header>
                    <div className={'flex items-center gap-4'}>
                        <SegmentListItemIcon className={'[&>*]:!text-blue-500'} />
                        <Drawer.Title>Segments</Drawer.Title>
                    </div>
                </Drawer.Header>
                <Drawer.Body>
                    <div className={'flex flex-col gap-4'}>
                        <div className={'flex items-center justify-between gap-4'}>
                            <Input.Root size={'sm'}>
                                <Input.SearchIcon />
                                <Input.Form
                                    placeholder={'Search'}
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                />
                            </Input.Root>
                            <DropdownMenu.Root>
                                <DropdownMenu.Trigger className={'flex items-center gap-1.5'}>
                                    <p className={'text-sm font-medium text-gray-500'}>Sort</p>
                                    <FontAwesomeIcon
                                        size={'sm'}
                                        className={'text-gray-400'}
                                        icon={faChevronDown}
                                    />
                                </DropdownMenu.Trigger>
                                <DropdownMenu.Content sideOffset={5} align={'end'}>
                                    <DropdownMenu.Item
                                        className={'flex justify-between gap-4'}
                                        onClick={() => setSortState('asc')}
                                    >
                                        <div className={'flex items-center gap-1.5'}>
                                            <FontAwesomeIcon icon={ascIconFactory('alpha')} />
                                            <p>Sort ascending</p>
                                        </div>
                                        {sortState === 'asc' && (
                                            <FontAwesomeIcon
                                                size={'sm'}
                                                className={'text-blue-500'}
                                                icon={faCheck}
                                            />
                                        )}
                                    </DropdownMenu.Item>
                                    <DropdownMenu.Item
                                        className={'flex justify-between gap-4'}
                                        onClick={() => setSortState('desc')}
                                    >
                                        <div className={'flex items-center gap-1.5'}>
                                            <FontAwesomeIcon icon={descIconFactory('alpha')} />
                                            <p>Sort descending</p>
                                        </div>
                                        {sortState === 'desc' && (
                                            <FontAwesomeIcon
                                                size={'sm'}
                                                className={'text-blue-500'}
                                                icon={faCheck}
                                            />
                                        )}
                                    </DropdownMenu.Item>
                                    <DropdownMenu.Item
                                        className={'flex justify-between gap-4'}
                                        onClick={() => setSortState('newest')}
                                    >
                                        <p>Recently created</p>
                                        {sortState === 'newest' && (
                                            <FontAwesomeIcon
                                                size={'sm'}
                                                className={'text-blue-500'}
                                                icon={faCheck}
                                            />
                                        )}
                                    </DropdownMenu.Item>
                                    <DropdownMenu.Item
                                        className={'flex justify-between gap-4'}
                                        onClick={() => setSortState('oldest')}
                                    >
                                        <p>Oldest created</p>
                                        {sortState === 'oldest' && (
                                            <FontAwesomeIcon
                                                size={'sm'}
                                                className={'text-blue-500'}
                                                icon={faCheck}
                                            />
                                        )}
                                    </DropdownMenu.Item>
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                        </div>
                        <EmptySearch />
                        <EmptySegments />
                        <LoadingOverlay isLoading={isPending}>
                            <div className={'flex flex-col gap-4'}>
                                {savedSegments.map(seg => (
                                    <SegmentListItem key={seg.id}>
                                        <SegmentListItemButton
                                            onClick={() => {
                                                setIsEditing(false);
                                                setName(seg.name);
                                                setSegmentId(cacheKey, seg.id);
                                                setFilters(
                                                    cacheKey,
                                                    transformSavedFilterGroups(seg.filters),
                                                );
                                                setIsDrawerOpen(false);
                                            }}
                                        >
                                            <SegmentListItemIcon />
                                            <p className={'text-sm font-medium'}>{seg.name}</p>
                                        </SegmentListItemButton>
                                        <DropdownMenu.Root>
                                            <DropdownMenu.EllipsisTrigger />
                                            <DropdownMenu.Content align={'end'}>
                                                <DeleteSegmentDialog.Root segmentId={seg.id}>
                                                    <DeleteSegmentDialog.Trigger asChild={true}>
                                                        <DropdownMenu.Item
                                                            onSelect={e => e.preventDefault()}
                                                        >
                                                            Delete
                                                        </DropdownMenu.Item>
                                                    </DeleteSegmentDialog.Trigger>
                                                </DeleteSegmentDialog.Root>
                                            </DropdownMenu.Content>
                                        </DropdownMenu.Root>
                                    </SegmentListItem>
                                ))}
                            </div>
                        </LoadingOverlay>
                    </div>
                </Drawer.Body>
                <Drawer.Footer className={'justify-end'}>
                    <Drawer.Cancel asChild={true}>
                        <Button variant={'outline'} color={'neutral'}>
                            Cancel
                        </Button>
                    </Drawer.Cancel>
                </Drawer.Footer>
            </Drawer.Content>
        </Drawer.Root>
    );
};

interface SegmentListItemProps extends ComponentPropsWithoutRef<'div'> {}

const SegmentListItem = (props: SegmentListItemProps) => {
    const { className, children, ...rest } = props;

    return (
        <div
            className={twMerge(
                className,
                '-mx-1.5 flex items-center justify-between gap-1 rounded-lg px-1.5 py-1 hover:bg-gray-100',
            )}
            {...rest}
        >
            {children}
        </div>
    );
};

interface SegmentListItemButtonProps extends Omit<ComponentPropsWithoutRef<'button'>, 'color'> {}

const SegmentListItemButton = (props: SegmentListItemButtonProps) => {
    const { className, children, ...rest } = props;

    return (
        <Button
            variant="none"
            color="neutral"
            size="md"
            className={twMerge(className, 'flex-grow justify-start')}
            {...rest}
        >
            {children}
        </Button>
    );
};

interface SegmentListItemIconProps extends Omit<BoxIconProps, 'children' | 'size'> {}

const SegmentListItemIcon = (props: SegmentListItemIconProps) => {
    const { className, ...rest } = props;

    return (
        <BoxIcon size="lg" className={className} {...rest}>
            <FontAwesomeIcon className={'text-gray-400'} size={'lg'} icon={faGridRound2Plus} />
        </BoxIcon>
    );
};

const getSortStateForRequest = (sortState: SortState) => {
    switch (sortState) {
        case 'asc':
            return { sortBy: 'name' };
        case 'desc':
            return { sortByDesc: 'name' };
        case 'newest':
            return { sortByDesc: 'created_at' };
        case 'oldest':
            return { sortBy: 'created_at' };
    }
};

export { SegmentsIndexDrawer };
